<template>
  <material-card offset="75" class="ma-2 pa-3">
    <v-card-text style="position: relative">
      <div v-if="login">
        <p class="subheading">{{ loginTitle }}</p>

        <p class="title">{{ login }}</p>
      </div>

      <div v-if="partner && partner.name">
        <p class="subheading">Nazwa Partnera</p>

        <p class="title">{{ partner.name }}</p>
      </div>

      <div v-if="profile.firstname || profile.lastName">
        <p class="subheading">Imię i nazwisko</p>

        <p class="title">{{ profile.firstName }} {{ profile.lastName }}</p>
      </div>

      <div v-if="profile.city">
        <p class="subheading">Adres</p>

        <p class="title">{{ profile | address }}</p>
      </div>

      <div v-if="profile.email && profile.email !== login">
        <p class="subheading">E-mail (kontaktowy)</p>

        <p class="title">{{ profile.email }}</p>
      </div>

      <div v-if="profile.phoneNumber">
        <p class="subheading">Numer telefonu</p>

        <p class="title">{{ profile.phoneNumber }}</p>
      </div>

      <div v-if="profile.pesel">
        <p class="subheading">Pesel</p>

        <p class="title">{{ profile.pesel }}</p>
      </div>

      <div v-if="profile.gender">
        <p class="subheading">Płeć</p>

        <p class="title">{{ profile.gender | gender }}</p>
      </div>

      <div v-if="profile.birthDate">
        <p class="subheading">Data urodzenia</p>

        <p class="title">{{ profile.birthDate }}</p>
      </div>
    </v-card-text>

    <v-layout justify-center row wrap slot="actions">
      <check-permission joinType="OR" entityType="profile" :permissions="updatePermissions" :entityId="profile.id">
        <v-btn dark flat color="blue darken-2" @click="showEditModal"> <v-icon left> edit </v-icon> Edytuj </v-btn>
      </check-permission>

      <check-permission entityType="profile" permissions="USER.DELETE" :entityId="profile.id">
        <v-btn dark flat color="red darken-2" @click="showDeleteDialog">
          <v-icon left>mdi-trash-can-outline</v-icon>Usuń konto
        </v-btn>
      </check-permission>

      <check-permission
        v-if="profile.email"
        permissions="ACCOUNT.PASSWORD.RESET"
        entityType="profile"
        :entityId="profile.id"
      >
        <v-btn dark flat color="blue darken-2" @click="showPasswordDialog">
          <v-icon left>lock</v-icon>Resetuj hasło
        </v-btn>
      </check-permission>

      <check-permission
        v-if="userActive"
        permissions="ACCOUNT.PASSWORD.CHANGE"
        entityType="profile"
        :entityId="profile.id"
      >
        <v-btn dark flat color="blue darken-2" @click="openModal('changePassword')">
          <v-icon left>lock</v-icon>Zmień hasło
        </v-btn>
      </check-permission>

      <check-permission
        v-if="userToConfirmManually"
        permissions="ACCOUNT.CONFIRM_MANUALLY"
        entityType="profile"
        :entityId="profile.id"
      >
        <v-btn dark flat color="blue darken-2" @click="openModal('confirmUser')">
          <v-icon left>check</v-icon>Potwierdź konto
        </v-btn>
      </check-permission>

      <check-permission
        v-if="roleLinkField"
        joinType="OR"
        entityType="profile"
        :entityId="profile.id"
        :permissions="['USER.ROLE_MANAGE', 'PERMISSION_RELATION.ADD', 'PERMISSION_RELATION.DELETE']"
      >
        <router-link :to="{ name: roleLinkField, params: { [`${roleLinkParam}`]: profile.userId } }">
          <v-btn dark flat color="blue darken-2"> <v-icon left>mdi-sitemap</v-icon>role </v-btn>
        </router-link>
      </check-permission>
    </v-layout>

    <material-dialog
      cancelText="Anuluj"
      confirmText="Usuń"
      title="Potwierdź usunięcie"
      :text="passwordText"
      :id="profile.id"
      :show="profiledeleteDialog"
      @close="hideDeleteDialog"
      @confirm="deleteProfile"
    />

    <material-dialog
      cancelText="Anuluj"
      confirmText="Wyślij"
      title="Reset hasła"
      :text="passwordText"
      :width="450"
      :id="profile.id"
      :show="passwordDialog"
      @close="hidePasswordDialog"
      @confirm="sendConfirmation"
    />

    <change-password-modal
      :show="modals.changePassword"
      @close="closeModal('changePassword')"
      @change-password="changePassword($event)"
    />

    <confirm-user-modal
      :show="modals.confirmUser"
      @close="closeModal('confirmUser')"
      @confirm-user="confirmUser($event)"
    />
  </material-card>
</template>

<script>
import { address, formatError, GenderTranslation, isOfficial, UserState } from '@witkac/kartamiejska-shared';

import ChangePasswordModal from '@/components/helpers/ChangePasswordModal';
import ConfirmUserModal from '@/components/helpers/ConfirmUserModal';
import MaterialCard from '@/components/material/Card';
import MaterialDialog from '@/components/material/Dialog';
import { deleteProfileMixin } from '@/mixins/deleteProfile';
import { displayModalMixin } from '@/mixins/displayModal';
import { notificationsMixin } from '@/mixins/notifications';
import { resetPasswordMixin } from '@/mixins/resetPassword';

// @vuese
// Wyświetlanie tabeli z danymi klienta
// @group Client-Profile
export default {
  components: { ChangePasswordModal, MaterialCard, MaterialDialog, ConfirmUserModal },
  mixins: [deleteProfileMixin, displayModalMixin, notificationsMixin, resetPasswordMixin],
  props: {
    // Tytuł do loginu
    loginTitle: {
      type: String,
      required: false,
      default: 'Login'
    },
    // Login użytkownika
    login: {
      type: String,
      required: false
    },
    // Dane profilu
    profile: {
      type: Object,
      required: true
    },
    // Dane partnera
    partner: {
      type: Object,
      required: false
    },
    // Link do roli
    roleLinkField: {
      type: String,
      required: false,
      default: undefined
    },
    // Param roli
    roleLinkParam: {
      type: String,
      required: false,
      default: 'id'
    },
    // Uprawnienia do edycji
    updatePermissions: {
      type: [Array, String],
      required: true
    }
  },
  data() {
    return {
      UserState
    };
  },
  methods: {
    // @vuese
    // Funkcja wywołuje modal do edycji danych klienta
    showEditModal() {
      this.$emit('showEditModal');
    },

    // @vuese
    // Zmiana hasła
    changePassword(data) {
      this.$store
        .dispatch('auth/changePassword', { ...data, userId: this.userId })
        .then(() => {
          this.notifySuccess('Zmieniono hasło użytkownika.');
          this.closeModal('changePassword');
        })
        .catch(error => this.notifyError(formatError(error)));
    },
    // @vuese
    // Potwierdzenie użytkownika
    confirmUser(data) {
      this.$store
        .dispatch('auth/confirmManually', { password: data.password, userId: this.userId })
        .then(() => this.$router.go())
        .catch(error => this.notifyError(formatError(error)));
    }
  },
  computed: {
    isOfficial,
    userId() {
      return this.profile.userId;
    },
    userActive() {
      return this.profile.user.state === UserState.ACTIVE;
    },
    userToConfirmManually() {
      return !this.profile.email && this.profile.user.state === UserState.NOT_CONFIRMED;
    }
  },
  filters: {
    gender(val) {
      return GenderTranslation[val];
    },
    address
  }
};
</script>
